import React, { useEffect, useMemo, useState } from "react";
import TimeSlot from "./time_slot";
import WorkingHoursCompoundSelect from "./working_hour_compound_select";
import V2TabellaTurnoGiornaliero from "./v2_tabella_visualizza_turno_giornaliero";
import { formatHoursAndMinutes, getShiftDurationWithMidnight } from "../utils/workTimeCount";
import { getUiErrorMessage } from "../services/utils";
import { tryUpsertCustomSlot } from "../utils/useWeeklyShiftEditor";
import { buildBreaksPreview } from "./build_breaks_preview";
import Swal from "sweetalert2";

import {
    applyWorkingHourToDay,
    clearDay,
    deleteSlotGroup,
    getEditingOriginalByGroup,
    getVisiblePiecesForDay,
} from "../utils/useWeeklyShiftEditor";

export default function BuildWeekShiftPreview({
    token,
    open,
    dayLabel,
    selectedOu,
    allowHiddenBreaks,
    daysData,
    setDaysData,
    onClose,
}) {
    const [mode, setMode] = useState("working_hour");
    const [selectedWorkingHour, setSelectedWorkingHour] = useState(null);

    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editorKey, setEditorKey] = useState(0);
    const [showEditor, setShowEditor] = useState(true);

    // 1) init SOLO quando apro la modale o cambio giorno
    useEffect(() => {
    if (!open || !dayLabel) return;

    const cfg = daysData?.[dayLabel];

    // setto la mode una sola volta in base al contenuto attuale
    setMode(cfg?.type || "working_hour");

    // inizializzo turno selezionato solo se sono in working_hour
    setSelectedWorkingHour(cfg?.type === "working_hour" ? (cfg.workingHourObj || null) : null);

    setEditingGroupId(null);
    setEditorKey((k) => k + 1);
    }, [open, dayLabel]);

    // 2) quando daysData cambia, aggiorno SOLO selectedWorkingHour (senza toccare mode)
    useEffect(() => {
    if (!open || !dayLabel) return;

    const cfg = daysData?.[dayLabel];
    if (cfg?.type === "working_hour") {
        setSelectedWorkingHour(cfg.workingHourObj || null);
    } else {
        setSelectedWorkingHour(null);
    }
    }, [open, dayLabel, daysData]);

    // blocca scroll body
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => (document.body.style.overflow = prev);
    }, [open]);

    const { visible } = useMemo(() => {
        if (!dayLabel) return { visible: [] };
        return getVisiblePiecesForDay(daysData, dayLabel);
    }, [daysData, dayLabel]);

    const hasAnyVisible = visible.length > 0;

    useEffect(() => {
        if (!open) return;
        setShowEditor(!hasAnyVisible);
    }, [open, hasAnyVisible, dayLabel]);

    const editingOriginal = useMemo(() => {
        if (!editingGroupId) return null;
        return getEditingOriginalByGroup(daysData, editingGroupId);
    }, [daysData, editingGroupId]);

    const handleClearDay = () => {
        setDaysData((prev) => clearDay(prev, dayLabel));
        setMode("custom");
        setSelectedWorkingHour(null);
        setEditingGroupId(null);
        setShowEditor(true);
        setEditorKey((k) => k + 1);
    };

    const handleApplyWorkingHour = () => {
        if (!selectedWorkingHour?.id) return;
        setDaysData((prev) => applyWorkingHourToDay(prev, dayLabel, selectedWorkingHour));
        onClose?.();
    };

    const handleDeleteGroup = (groupId) => {
        setDaysData((prev) => deleteSlotGroup(prev, groupId));
    };

    const handleSaveSlot = (rawSlot) => {
        const anchorDay = editingOriginal?.anchorDay || dayLabel;

        // Usa funzione "try" che NON deve lanciare
        const res = tryUpsertCustomSlot(daysData, anchorDay, rawSlot, editingGroupId);

        if (!res?.ok) {
            Swal.fire({
            icon: "error",
            title: "Operazione non valida",
            text: getUiErrorMessage(res?.error),
            });
            return;
        }

        // aggiorna stato senza callback: niente throw in setState
        setDaysData(res.data);

        // reset UI solo se OK
        setEditingGroupId(null);
        setShowEditor(false);
        setEditorKey((k) => k + 1);
    };

    const handleClose = () => {
        setMode("working_hour");
        setSelectedWorkingHour(null);
        setEditingGroupId(null);
        setShowEditor(true);
        setEditorKey((k) => k + 1);
        onClose?.();
    };

    if (!open || !dayLabel) return null;

    return (
        <div
            className="fixed inset-0 z-[999] bg-black/40 px-4 py-6 overflow-y-auto"
            role="dialog"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) handleClose();
            }}
            style={{ marginTop: 0 }}
        >
            <div className="mx-auto w-full max-w-4xl">
                <div className="rounded-xl bg-white shadow-xl border border-gray-200 overflow-visible">
                    {/* HEADER */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 rounded-t-xl bg-indigo-100">
                        <div className="text-md font-semibold uppercase text-indigo-800">{dayLabel}</div>
                        <div className="flex items-center gap-2">
                            {hasAnyVisible && (
                                <button
                                    type="button"
                                    onClick={handleClearDay}
                                    className="rounded-lg border border-gray-200 bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-400 uppercase"
                                >
                                    Svuota giorno
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleClose}
                                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 uppercase"
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>

                    {/* BODY */}
                    <div className="px-5 py-4 space-y-4">
                        {/* Tabs */}
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setMode("working_hour")}
                                className={`px-3 py-2 rounded-lg text-sm font-semibold border focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                                mode === "working_hour"
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                }`}
                            >
                                Associa Turno Giornaliero
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                setMode("custom");
                                setEditingGroupId(null);
                                setShowEditor(!hasAnyVisible);
                                setEditorKey((k) => k + 1);
                                }}
                                className={`px-3 py-2 rounded-lg text-sm font-semibold border focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                                mode === "custom"
                                    ? "bg-indigo-600 text-white border-indigo-600"
                                    : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                }`}
                            >
                                Fasce Manuali
                            </button>
                        </div>

                        {mode === "working_hour" ? (
                            <div className="rounded-lg border border-gray-200 bg-white p-4 overflow-visible">
                                <div className="text-sm font-semibold uppercase text-indigo-800">Turno giornaliero da associare</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Seleziona un turno giornaliero. Questo sostituisce eventuali fasce manuali su quel giorno.
                                </div>

                                <div className="mt-3 relative z-[1000] overflow-visible">
                                    <WorkingHoursCompoundSelect
                                        key={`wh-select-${selectedOu?.id || "generic"}`}
                                        token={token}
                                        permission="payrolls"
                                        sourceOu={selectedOu?.id ?? null}
                                        value={selectedWorkingHour}
                                        onChange={setSelectedWorkingHour}
                                    />
                                </div>

                                {selectedWorkingHour && (
                                    <div className="mt-3">
                                        <V2TabellaTurnoGiornaliero selectedWorkingHour={selectedWorkingHour} />
                                    </div>
                                )}

                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="button"
                                        disabled={!selectedWorkingHour?.id}
                                        onClick={handleApplyWorkingHour}
                                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                                    >
                                        Applica
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="rounded-lg border border-gray-200 bg-white p-4">
                                    {/* editor header */}
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-semibold uppercase text-indigo-800">
                                            {editingGroupId ? "Modifica fascia" : "Aggiungi fascia"}
                                        </div>

                                        {!editingGroupId && hasAnyVisible && !showEditor && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingGroupId(null);
                                                    setShowEditor(true);
                                                    setEditorKey((k) => k + 1);
                                                }}
                                                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
                                            >
                                                + Aggiungi Fascia
                                            </button>
                                        )}
                                    </div>

                                    <div className="text-xs text-gray-500 mt-1">
                                        Se attraversa mezzanotte, verrà salvata in due pezzi (giorno corrente + successivo), ma resterà editabile come fascia unica.
                                    </div>

                                    {(showEditor || editingGroupId) && (
                                        <div className="mt-3">
                                            <TimeSlot
                                                key={`timeslot-${editorKey}-${editingGroupId || "new"}`}
                                                shiftPreview={
                                                    editingOriginal
                                                    ? {
                                                        workStartTime: editingOriginal.workStartTime,
                                                        workEndTime: editingOriginal.workEndTime,
                                                        breaks: editingOriginal.breaks || [],
                                                        }
                                                    : { workStartTime: "", workEndTime: "", breaks: [] }
                                                }
                                                allowHiddenBreaks={allowHiddenBreaks}
                                                onCancelEdit={() => {
                                                    setEditingGroupId(null);
                                                    setShowEditor(hasAnyVisible ? false : true);
                                                    setEditorKey((k) => k + 1);
                                                }}
                                                onShiftPreview={handleSaveSlot}
                                                onSaveEdit={() => {}}
                                                onAssignWh={"assignWhComponent"}
                                                onWeekShiftComponent={"weekShiftComponent"}
                                            />
                                        </div>
                                    )}

                                    {hasAnyVisible && (
                                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                            <div className="text-xs font-semibold uppercase text-gray-700 mb-2">Fasce inserite</div>

                                            <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                                                {visible.map((s) => (
                                                    <div key={`${s.groupId}-${s.__dayLabel}-${s.workStartTime}-${s.workEndTime}`} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 items-center gap-2">
                                                            <div className="text-sm font-semibold text-gray-800 min-w-0">
                                                                {s.workStartTime} - {s.workEndTime}
                                                                <span className="ml-2 text-[11px] font-semibold text-indigo-700">{s.__dayLabel}</span>
                                                            </div>

                                                            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 border border-indigo-100 hidden sm:inline-flex justify-self-center">
                                                                {formatHoursAndMinutes(getShiftDurationWithMidnight(s))}
                                                            </span>

                                                            <div className="flex w-full items-center justify-end gap-2 w-28">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setEditingGroupId(s.groupId);
                                                                        setShowEditor(true);
                                                                        setEditorKey((k) => k + 1);
                                                                    }}
                                                                    className="text-xs font-semibold text-indigo-700 hover:text-indigo-600"
                                                                >
                                                                    Modifica
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteGroup(s.groupId)}
                                                                    className="text-xs font-semibold text-red-600 hover:text-red-500"
                                                                >
                                                                    Elimina
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="mt-1">{buildBreaksPreview(s.breaks)}</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="mt-3 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={handleClose}
                                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-none focus:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                >
                                                    Fatto
                                                </button>
                                            </div>
                                        </div>
                                    )}                         
                                </div>
                                {!hasAnyVisible && (
                                    <div className="mt-3 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={handleClose}
                                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus:outline-none focus:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        >
                                            Fatto
                                        </button>
                                    </div>
                                )}
                            </>
                            
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}